import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';

import { ACCOUNT_MANAGEMENT_LE_ADMIN } from '../../../../constants/roles';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { AppExperiments, useIsTreatment } from '../../../../utils/experiments';
import { useMinuteTick } from '../../hooks/useMinuteTick';
import { useRoles } from '../../hooks/useRoles';
import { useTeamApi } from '../../hooks/useTeamApi';
import { getCancelPendingRoleErrorMessage } from '../../utils/cancelPendingRoleErrors';
import { getCancelPendingRoleSuccessMessage } from '../../utils/cancelPendingRoleMessages';
import type { PendingRole } from '../../utils/gracePeriod';
import { canCancelAllPendingRoles, getPendingRoles } from '../../utils/gracePeriod';

import { PendingRoleBanner } from './PendingRoleBanner';

const messages = defineMessages({
  fallbackRoleName: {
    id: 'Team.editMemberPendingRoleBanner.fallbackRoleName',
    defaultMessage: 'Admin',
    description:
      'Role name used in "Newly granted Admin access starts ..." when the Workspace admin role definition has not loaded',
  },
});

export type EditMemberPendingRoleBannerProps = {
  memberUuid: string;
  /** The member's pending roles from `normalizeWorkspaceMember`. */
  pendingRoles: readonly PendingRole[] | undefined;
  /**
   * Whether the current user may manage this entity. "Revoke admin access" needs it
   * for every entity with a pending role, since the cancellation covers all of them.
   */
  canCancelEntity: (legalEntityId: string) => boolean;
  /** Called after a successful cancel, once the member's roles have updated. */
  onCancelled?: () => void;
  /**
   * Called after a failed cancel. Some grants may still have been cancelled, so
   * the host re-syncs with the member's server roles. Save stays blocked until
   * the returned promise settles.
   */
  onCancelFailed?: () => Promise<void> | void;
  /** Called when a cancel request starts (true) and settles (false). */
  onCancellingChange?: (isCancelling: boolean) => void;
  /** Hides "Revoke admin access", e.g. while the tray is saving. */
  isCancelDisabled?: boolean;
};

/**
 * IDFE-4025 (t0018_4fba688a): the single `PendingRoleBanner` in the Edit
 * member tray. Fed every pending Workspace admin grant's start time across the
 * member's entities. "Revoke admin access" cancels the pending admin grants on
 * every entity the current user manages. Renders nothing in control or when
 * nothing is pending.
 */
export function EditMemberPendingRoleBanner({
  memberUuid,
  pendingRoles,
  canCancelEntity,
  onCancelled,
  onCancelFailed,
  onCancellingChange,
  isCancelDisabled = false,
}: EditMemberPendingRoleBannerProps) {
  const isGracePeriodTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);
  const toast = useToast();
  const { formatMessage } = useIntl();
  const { roleMap } = useRoles();
  const { selectedOrganization } = useOrganization();
  const { cancelPendingRoles } = useTeamApi(selectedOrganization?.organizationId ?? '');
  const [isCancelling, setIsCancelling] = useState(false);

  // Fetch-time snapshot; PendingRoleBanner re-checks each start time against the clock.
  // Every pending role counts, not just admin: CancelPendingRoleChange cancels them all.
  const livePendingRoles = useMemo(
    () =>
      isGracePeriodTreatment
        ? getPendingRoles({ pendingRoles: pendingRoles ?? [] }, new Date())
        : [],
    [isGracePeriodTreatment, pendingRoles],
  );

  const pendingAdmins = useMemo(
    () => livePendingRoles.filter(({ role }) => role === ACCOUNT_MANAGEMENT_LE_ADMIN),
    [livePendingRoles],
  );

  const effectiveAts = useMemo(
    () => pendingAdmins.map(({ effectiveAt }) => effectiveAt),
    [pendingAdmins],
  );

  // Read on every render so the cancel action disappears once the last pending
  // admin takes effect. It also needs manage rights on every entity that still
  // has a pending role, because the cancellation covers all of them.
  const now = new Date();
  const hasLivePendingAdmin = pendingAdmins.some(({ effectiveAt }) => effectiveAt > now);
  const canCancel =
    !isCancelDisabled &&
    hasLivePendingAdmin &&
    canCancelAllPendingRoles(livePendingRoles, now, canCancelEntity);
  useMinuteTick(hasLivePendingAdmin);

  const handleCancel = useCallback(() => {
    if (!canCancel) return;

    setIsCancelling(true);
    onCancellingChange?.(true);
    cancelPendingRoles(memberUuid)
      .then((outcome) => {
        logEvent('member_pending_role_cancelled_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
          source: 'member_page',
        });
        toast.show(formatMessage(getCancelPendingRoleSuccessMessage(outcome)));
        onCancelled?.();
      })
      .catch(async (error: unknown) => {
        const rawError = error instanceof Error ? error.message : undefined;
        logEvent('member_pending_role_cancelled_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          source: 'member_page',
          error: rawError ?? String(error),
        });
        toast.show(formatMessage(getCancelPendingRoleErrorMessage(rawError)));
        await onCancelFailed?.();
      })
      .catch(() => undefined)
      .finally(() => {
        setIsCancelling(false);
        onCancellingChange?.(false);
      });
  }, [
    cancelPendingRoles,
    canCancel,
    formatMessage,
    memberUuid,
    onCancelFailed,
    onCancelled,
    onCancellingChange,
    toast,
  ]);

  if (pendingAdmins.length === 0) {
    return null;
  }

  // Space from the role rows above, as in the design.
  return (
    <Box paddingTop={2}>
      <PendingRoleBanner
        roleName={
          roleMap[ACCOUNT_MANAGEMENT_LE_ADMIN]?.name ?? formatMessage(messages.fallbackRoleName)
        }
        effectiveAts={effectiveAts}
        canCancel={canCancel}
        onCancel={handleCancel}
        isCancelling={isCancelling}
      />
    </Box>
  );
}
